import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Badge, Button, Card, Field, Grid2, HeroPill, Input, MobileHero, MobilePage, SectionHeader, Skeleton, TextArea } from '../../components/mobile/MobilePageKit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SPECIALIZATIONS = ['Clinical Psychology', 'Counselling', 'Neuropsychology', 'Child Psychology', 'Addiction', 'Trauma', 'Assessment', 'Family Therapy'];

const MobileProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requestingChange, setRequestingChange] = useState(false);
  const [changeRequestPending, setChangeRequestPending] = useState(false);
  const [profileLocked, setProfileLocked] = useState(true);
  const [uploadsLocked, setUploadsLocked] = useState(true);
  const [profile, setProfile] = useState(null);
  const [files, setFiles] = useState({ cv: null, licenseDocument: null, profileImage: null });
  const cvInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', phone: '', licenseNumber: '', licenseType: '', licenseState: '', licenseExpiry: '', yearsOfExperience: '', workplace: '', workplaceAddress: '', bio: '', specializations: []
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/practitioner/profile`);
        const data = response.data;
        setProfile(data);
        setChangeRequestPending(data.changeRequestPending || false);
        setProfileLocked(!data.changeRequestApproved);
        setUploadsLocked(!data.changeRequestApproved);
        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          licenseNumber: data.licenseNumber || '',
          licenseType: data.licenseType || '',
          licenseState: data.licenseState || '',
          licenseExpiry: data.licenseExpiry ? data.licenseExpiry.split('T')[0] : '',
          yearsOfExperience: data.yearsOfExperience || '',
          workplace: data.workplace || '',
          workplaceAddress: data.workplaceAddress || '',
          bio: data.bio || '',
          specializations: data.specializations || [],
        });
      } catch (error) {
        console.error('Mobile profile load error:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getAuthenticatedFileUrl = (fileRef) => {
    if (!fileRef) return null;
    const token = localStorage.getItem('token') || localStorage.getItem('admin_token') || axios.defaults.headers.common.Authorization?.replace(/^Bearer\s+/i, '')?.trim();
    const normalizedRef = typeof fileRef === 'string' && fileRef.includes('/') ? fileRef.replace(/^\/+/, '') : fileRef;
    const baseUrl = `${API_URL}/files/download/${normalizedRef}`;
    return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
  };

  const handleFileSelect = (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (field === 'licenseDocument') {
      const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowed.includes(file.type)) { toast.error('Please select a PDF or image file'); return; }
    } else if (field === 'cv' && file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be less than 5MB'); return; }
    setFiles(prev => ({ ...prev, [field]: file }));
    toast.success(`${field === 'cv' ? 'CV' : field === 'profileImage' ? 'Profile photo' : 'License document'} selected`);
  };

  const toggleSpecialization = (spec) => {
    if (profileLocked) return;
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(item => item !== spec)
        : [...prev.specializations, spec],
    }));
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, key === 'specializations' ? JSON.stringify(formData[key]) : formData[key] || '');
      });
      if (files.cv) submitData.append('cv', files.cv);
      if (files.licenseDocument) submitData.append('licenseDocument', files.licenseDocument);
      if (files.profileImage) submitData.append('profileImage', files.profileImage);
      await axios.put(`${API_URL}/practitioner/profile`, submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Profile updated successfully');
      setFiles({ cv: null, licenseDocument: null, profileImage: null });
      setProfileLocked(true);
      setUploadsLocked(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const requestChange = async () => {
    try {
      setRequestingChange(true);
      await axios.post(`${API_URL}/practitioner/profile/request-change`, { message: 'I am requesting permission to update my profile documents.' });
      toast.success('Change request sent');
      setChangeRequestPending(true);
      setUploadsLocked(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send change request');
    } finally {
      setRequestingChange(false);
    }
  };

  return (
    <MobilePage>
      <MobileHero>
        <HeroPill>Account Settings</HeroPill>
        <h2>Profile</h2>
        <p>Keep your practitioner identity, license, workplace, and payout contact details current.</p>
      </MobileHero>

      {loading ? <Skeleton $height="360px" /> : (
        <>
          <StatusCard>
            <div><strong>{formData.firstName} {formData.lastName}</strong><span>{formData.workplace || 'Practitioner profile'}</span></div>
            <Badge $tone={profileLocked ? 'pending' : 'paid'}>{profileLocked ? 'Locked' : 'Editable'}</Badge>
          </StatusCard>

          <Card>
            <SectionHeader><div><span>Documents</span><h3>Your Current Documents</h3></div></SectionHeader>
            <DocumentGrid>
              <DocumentButton type="button" onClick={() => profile?.profileImagePath && window.open(getAuthenticatedFileUrl(profile.profileImagePath), '_blank')}><strong>Profile Photo</strong><span>{profile?.profileImagePath ? '✓ Uploaded · Tap to view' : '— Not uploaded'}</span></DocumentButton>
              <DocumentButton type="button" onClick={() => profile?.licenseDocumentPath && window.open(getAuthenticatedFileUrl(profile.licenseDocumentPath), '_blank')}><strong>License Document</strong><span>{profile?.licenseDocumentPath ? '✓ Uploaded · Tap to view' : '— Not uploaded'}</span></DocumentButton>
              <DocumentButton type="button" onClick={() => profile?.cvPath && window.open(getAuthenticatedFileUrl(profile.cvPath), '_blank')}><strong>CV</strong><span>{profile?.cvPath ? '✓ Uploaded · Tap to view' : '— Not uploaded'}</span></DocumentButton>
            </DocumentGrid>
          </Card>

          {profileLocked && (
            <Card>
              <SectionHeader><div><span>Edit access</span><h3>Profile locked</h3></div></SectionHeader>
              <p style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.45, marginTop: 0 }}>Request admin permission before editing protected profile details.</p>
              <Button $full disabled={requestingChange || changeRequestPending} type="button" onClick={requestChange}>{changeRequestPending ? 'Request pending' : requestingChange ? 'Sending…' : 'Request change access'}</Button>
            </Card>
          )}

          <Card>
            <SectionHeader><div><span>Personal</span><h3>Basic details</h3></div></SectionHeader>
            <FormStack>
              <Grid2><Field>First name<Input disabled={profileLocked} name="firstName" value={formData.firstName} onChange={handleChange} /></Field><Field>Last name<Input disabled={profileLocked} name="lastName" value={formData.lastName} onChange={handleChange} /></Field></Grid2>
              <Field>Phone<Input disabled={profileLocked} name="phone" value={formData.phone} onChange={handleChange} /></Field>
              <Field>Bio<TextArea disabled={profileLocked} name="bio" value={formData.bio} onChange={handleChange} placeholder="Professional bio" /></Field>
            </FormStack>
          </Card>

          <Card>
            <SectionHeader><div><span>License</span><h3>Professional details</h3></div></SectionHeader>
            <FormStack>
              <Grid2><Field>License no.<Input disabled={profileLocked} name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} /></Field><Field>Type<Input disabled={profileLocked} name="licenseType" value={formData.licenseType} onChange={handleChange} /></Field></Grid2>
              <Grid2><Field>State<Input disabled={profileLocked} name="licenseState" value={formData.licenseState} onChange={handleChange} /></Field><Field>Expiry<Input disabled={profileLocked} name="licenseExpiry" value={formData.licenseExpiry} onChange={handleChange} type="date" /></Field></Grid2>
              <Field>Years of experience<Input disabled={profileLocked} name="yearsOfExperience" value={formData.yearsOfExperience} onChange={handleChange} type="number" /></Field>
            </FormStack>
          </Card>

          <Card>
            <SectionHeader><div><span>Practice</span><h3>Workplace</h3></div></SectionHeader>
            <FormStack>
              <Field>Workplace<Input disabled={profileLocked} name="workplace" value={formData.workplace} onChange={handleChange} /></Field>
              <Field>Workplace address<TextArea disabled={profileLocked} name="workplaceAddress" value={formData.workplaceAddress} onChange={handleChange} /></Field>
            </FormStack>
          </Card>

          <Card>
            <SectionHeader><div><span>Uploads</span><h3>Update Documents</h3></div></SectionHeader>
            <UploadGrid>
              <UploadBox type="button" disabled={uploadsLocked} onClick={() => !uploadsLocked && licenseInputRef.current?.click()} $hasFile={!!files.licenseDocument}><input ref={licenseInputRef} type="file" accept=".pdf,image/*" onChange={event => handleFileSelect(event, 'licenseDocument')} /><strong>{files.licenseDocument ? 'New license selected' : 'Upload License'}</strong><span>{uploadsLocked ? 'Uploads locked — request permission above' : files.licenseDocument?.name || 'PDF or image, max 5MB'}</span></UploadBox>
              <UploadBox type="button" disabled={uploadsLocked} onClick={() => !uploadsLocked && profileImageInputRef.current?.click()} $hasFile={!!files.profileImage}><input ref={profileImageInputRef} type="file" accept="image/*" onChange={event => handleFileSelect(event, 'profileImage')} /><strong>{files.profileImage ? 'New photo selected' : 'Upload Photo'}</strong><span>{uploadsLocked ? 'Uploads locked — request permission above' : files.profileImage?.name || 'Image, max 5MB'}</span></UploadBox>
              <UploadBox type="button" disabled={uploadsLocked} onClick={() => !uploadsLocked && cvInputRef.current?.click()} $hasFile={!!files.cv}><input ref={cvInputRef} type="file" accept=".pdf,application/pdf" onChange={event => handleFileSelect(event, 'cv')} /><strong>{files.cv ? 'New CV selected' : 'Upload CV'}</strong><span>{uploadsLocked ? 'Uploads locked — request permission above' : files.cv?.name || 'PDF, max 5MB'}</span></UploadBox>
            </UploadGrid>
          </Card>

          <Card>
            <SectionHeader><div><span>Expertise</span><h3>Specializations</h3></div></SectionHeader>
            <ChipGrid>{SPECIALIZATIONS.map(spec => <Chip key={spec} type="button" disabled={profileLocked} onClick={() => toggleSpecialization(spec)} $active={formData.specializations.includes(spec)}>{spec}</Chip>)}</ChipGrid>
          </Card>

          {!uploadsLocked && <Button $full disabled={saving} type="button" onClick={saveProfile}>{saving ? 'Saving…' : 'Save profile changes'}</Button>}
          {uploadsLocked && profile?.approvalStatus === 'approved' && !changeRequestPending && <LockedHint>🔒 Uploads are locked. Request permission above to make changes.</LockedHint>}
        </>
      )}
    </MobilePage>
  );
};

const StatusCard = styled.section`
  align-items: center;
  background: white;
  border: 1px solid #e8f0fe;
  border-radius: 24px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  display: flex;
  justify-content: space-between;
  padding: 15px;
  strong { color: #0f172a; display: block; font-size: 1rem; }
  span { color: #64748b; display: block; font-size: 0.78rem; margin-top: 4px; }
`;

const DocumentGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const DocumentButton = styled.button`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 12px;
  text-align: left;
  strong { color: #0f172a; display: block; font-size: 0.88rem; }
  span { color: #64748b; display: block; font-size: 0.76rem; margin-top: 4px; }
`;

const UploadGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const UploadBox = styled.button`
  background: ${p => (p.$hasFile ? '#eff6ff' : '#f8fafc')};
  border: 1px dashed ${p => (p.$hasFile ? '#93c5fd' : '#cbd5e1')};
  border-radius: 18px;
  min-height: 72px;
  padding: 13px;
  text-align: left;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  input { display: none; }
  strong { color: #0f172a; display: block; font-size: 0.88rem; }
  span { color: #64748b; display: block; font-size: 0.76rem; margin-top: 4px; }
`;

const LockedHint = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 18px;
  color: #92400e;
  font-size: 0.8rem;
  font-weight: 850;
  padding: 13px;
`;

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  background: ${p => (p.$active ? '#dbeafe' : '#f8fafc')};
  border: 1px solid ${p => (p.$active ? '#93c5fd' : '#e2e8f0')};
  border-radius: 999px;
  color: ${p => (p.$active ? '#075985' : '#475569')};
  font-size: 0.76rem;
  font-weight: 850;
  padding: 9px 11px;
`;

export default MobileProfile;
